import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
    Alert,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import TuneIcon from '@mui/icons-material/Tune';
import {
    formatStartingValue,
    formatStartingValueInput,
    formatTradeIdeaRange,
    parseStartingValueInputs,
    STARTING_VALUE_INPUTS,
    startingValueInputs,
    SUGGESTION_BASIS_FIELDS,
    tradeIdeaRangeLimit,
} from '../../utils/posSizingStartingValues';
import { posSizingActionButtonSx } from './tableStyles';
import { startingValuesShape } from './PosSizingStartingValuesDialog';

const rowButtonSx = { textTransform: 'none', whiteSpace: 'nowrap', mt: 0.25 };

export function SizingDefaultsDialog({
    currentValues,
    initialValues,
    suggestion: openingSuggestion = null,
    canPickTargetVolDollar = false,
    onSuggest,
    onSave,
    onClose,
}) {
    const [inputs, setInputs] = useState(() => startingValueInputs(currentValues));
    const [pickedSuggestion, setPickedSuggestion] = useState(null);
    const [picking, setPicking] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const parsed = parseStartingValueInputs(inputs);
    const rangeText = formatTradeIdeaRange(
        tradeIdeaRangeLimit(pickedSuggestion ?? openingSuggestion, parsed.values),
    );
    const busy = saving || picking;

    const setInput = (field, value) => {
        setInputs((current) => ({ ...current, [field]: value }));
    };

    const handlePick = async () => {
        if (SUGGESTION_BASIS_FIELDS.some((field) => parsed.errors[field])) {
            setError('Fix the other values first.');
            return;
        }
        setPicking(true);
        setError('');
        try {
            const next = await onSuggest(parsed.valid);
            setPickedSuggestion(next);
            setInput(
                'target_vol_dollar',
                formatStartingValueInput('target_vol_dollar', next.targetVolDollar),
            );
        } catch (pickError) {
            console.error('Error picking Target Vol ($):', pickError);
            setError('A Target Vol ($) could not be picked. Try again.');
        } finally {
            setPicking(false);
        }
    };

    const handleSave = async () => {
        if (!parsed.values) return;
        setSaving(true);
        setError('');
        try {
            await onSave(parsed.values);
            onClose();
        } catch (saveError) {
            console.error('Error saving sizing defaults:', saveError);
            setError(saveError?.message || 'Your sizing defaults could not be saved. Try again.');
            setSaving(false);
        }
    };

    return (
        <Dialog
            open
            onClose={() => { if (!busy) onClose(); }}
            maxWidth="sm"
            fullWidth
            aria-labelledby="sizing-defaults-title"
        >
            <DialogTitle id="sizing-defaults-title">Sizing defaults</DialogTitle>
            <DialogContent>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Max Pos Size and Normal Vol apply to your draft. A changed Target
                    Vol or Scaler applies to every Sizing Targets category.
                </Typography>
                <Box sx={{ display: 'grid', gap: 1.5 }}>
                    {STARTING_VALUE_INPUTS.map(({ field, label }) => {
                        const fieldError = parsed.errors[field] || '';
                        return (
                            <Box key={field}>
                                {/* On a narrow screen the buttons wrap below the input. */}
                                <Box
                                    sx={{
                                        display: 'flex',
                                        flexWrap: 'wrap',
                                        alignItems: 'flex-start',
                                        gap: 1,
                                    }}
                                >
                                    <TextField
                                        label={label}
                                        size="small"
                                        value={inputs[field]}
                                        onChange={(event) => setInput(field, event.target.value)}
                                        error={Boolean(fieldError)}
                                        helperText={fieldError || `Initial choice: ${
                                            formatStartingValue(field, initialValues[field])
                                        }`}
                                        disabled={busy}
                                        inputProps={{ inputMode: 'decimal' }}
                                        sx={{ flex: '1 1 200px' }}
                                    />
                                    {field === 'target_vol_dollar' && canPickTargetVolDollar ? (
                                        <Button
                                            size="small"
                                            onClick={handlePick}
                                            disabled={busy}
                                            sx={rowButtonSx}
                                        >
                                            Pick for me
                                        </Button>
                                    ) : null}
                                    <Button
                                        size="small"
                                        onClick={() => setInput(
                                            field,
                                            formatStartingValueInput(field, initialValues[field]),
                                        )}
                                        disabled={busy}
                                        aria-label={`Restore ${label} to default`}
                                        sx={rowButtonSx}
                                    >
                                        Restore to default
                                    </Button>
                                </Box>
                                {field === 'target_vol_dollar' && rangeText ? (
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{ mt: 0.5 }}
                                    >
                                        {rangeText}
                                    </Typography>
                                ) : null}
                            </Box>
                        );
                    })}
                </Box>
                {error ? <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert> : null}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={onClose} disabled={busy} sx={{ textTransform: 'none' }}>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    onClick={handleSave}
                    disabled={busy || !parsed.values}
                    sx={{ textTransform: 'none' }}
                >
                    Save
                </Button>
            </DialogActions>
        </Dialog>
    );
}

SizingDefaultsDialog.propTypes = {
    currentValues: startingValuesShape.isRequired,
    initialValues: startingValuesShape.isRequired,
    suggestion: PropTypes.object,
    canPickTargetVolDollar: PropTypes.bool,
    onSuggest: PropTypes.func.isRequired,
    onSave: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired,
};

/** The Position Sizing toolbar button that opens the Sizing defaults dialog. */
export default function SizingDefaultsControl({
    currentValues = null,
    initialValues = null,
    canPickTargetVolDollar = false,
    onSuggest,
    onSave,
    disabled = false,
}) {
    const [open, setOpen] = useState(false);
    const [suggestion, setSuggestion] = useState(null);
    const openRequestRef = useRef(0);
    const ready = Boolean(currentValues && initialValues);

    const handleOpen = () => {
        const request = openRequestRef.current + 1;
        openRequestRef.current = request;
        setSuggestion(null);
        setOpen(true);
        if (!canPickTargetVolDollar) return;
        // Preview where trade ideas fall at the current Target Vol ($).
        onSuggest(currentValues)
            .then((next) => {
                if (openRequestRef.current === request) setSuggestion(next);
            })
            .catch((error) => {
                console.error('Error previewing trade ideas:', error);
            });
    };

    const handleClose = () => {
        openRequestRef.current += 1;
        setOpen(false);
    };

    return (
        <>
            <Tooltip title="Sizing defaults">
                <span>
                    <IconButton
                        size="small"
                        onClick={handleOpen}
                        disabled={disabled || !ready}
                        aria-label="Sizing defaults"
                        sx={posSizingActionButtonSx}
                    >
                        <TuneIcon fontSize="small" />
                    </IconButton>
                </span>
            </Tooltip>
            {open && ready ? (
                <SizingDefaultsDialog
                    currentValues={currentValues}
                    initialValues={initialValues}
                    suggestion={suggestion}
                    canPickTargetVolDollar={canPickTargetVolDollar}
                    onSuggest={onSuggest}
                    onSave={onSave}
                    onClose={handleClose}
                />
            ) : null}
        </>
    );
}

SizingDefaultsControl.propTypes = {
    currentValues: startingValuesShape,
    initialValues: startingValuesShape,
    canPickTargetVolDollar: PropTypes.bool,
    onSuggest: PropTypes.func.isRequired,
    onSave: PropTypes.func.isRequired,
    disabled: PropTypes.bool,
};
